import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Modal from '../Modal';
import {
  checkGate,
  errorCode,
  specError,
  useLearners,
  useSaveGate,
} from '../../hooks/useSpecialization';
import {
  CARD,
  INPUT,
  PRIMARY,
  RULES,
  SECONDARY,
  configForApi,
  formatDateTime,
  groupProblems,
  newRule,
  normaliseConfig,
  plural,
  ruleLabel,
  ruleUnavailable,
  sameConfig,
} from './specText';

const CHECK_DELAY_MS = 600;

function Problems({ problems }) {
  if (!problems?.length) return null;
  return (
    <ul className="m-0 list-none space-y-0.5 p-0">
      {problems.map((problem) => (
        <li
          key={`${problem.code}-${problem.field}-${problem.message}`}
          className="text-xs text-red-700"
        >
          {problem.message}
        </li>
      ))}
    </ul>
  );
}

function Section({ title, help, children }) {
  return (
    <section className={`${CARD} p-5 space-y-4`}>
      <div>
        <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        {help && <p className="text-xs text-gray-500">{help}</p>}
      </div>
      {children}
    </section>
  );
}

const contentSummary = (item) =>
  [
    plural(item.weeks, 'week'),
    plural(item.quizzes, 'quiz', 'quizzes'),
    plural(item.assignments, 'assignment'),
  ].join(' · ');

function ModuleRow({ module, checked, onToggle, readOnly }) {
  let blocked = null;
  if (module.missing) blocked = 'Deleted';
  else if (module.is_specialization) blocked = 'Specialization module';
  const disabled = readOnly || (Boolean(blocked) && !checked);
  return (
    <li className="flex items-start gap-3 py-2">
      {!readOnly && (
        <input
          type="checkbox"
          className="mt-1"
          checked={checked}
          disabled={disabled}
          onChange={onToggle}
          aria-label={module.name || module.module_id}
        />
      )}
      <div className="min-w-0 flex-1">
        <p className={`text-sm ${blocked ? 'text-gray-400' : 'text-gray-900'}`}>
          {module.name || module.module_id}
          {blocked && (
            <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-500">
              {blocked}
            </span>
          )}
        </p>
        <p className="text-xs text-gray-500">
          {module.tracks?.length
            ? module.tracks
                .map((t) => `${t.track_name}${t.is_core ? ' (core)' : ''}`)
                .join(', ')
            : 'Not in a track'}
          {' · '}
          {contentSummary(module)}
        </p>
      </div>
    </li>
  );
}

function ScopeSection({ draft, setDraft, modules, problems, onEditTracks }) {
  const isModules = draft.scope.type === 'modules';
  const selected = new Set(draft.scope.module_ids);
  const coreModules = modules.filter((m) => m.is_core && !m.is_specialization);
  const setType = (type) =>
    setDraft((d) => ({ ...d, scope: { ...d.scope, type } }));
  const toggle = (id) =>
    setDraft((d) => {
      const ids = new Set(d.scope.module_ids);
      if (ids.has(id)) ids.delete(id);
      else ids.add(id);
      return { ...d, scope: { ...d.scope, module_ids: [...ids] } };
    });

  return (
    <Section
      title="Which modules the rules measure"
      help="Learners must meet the rules in these modules before choosing. Specialization modules can't be included."
    >
      <div className="flex flex-wrap gap-4 text-sm">
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="scope"
            checked={!isModules}
            onChange={() => setType('core_tracks')}
          />
          The cohort&apos;s core tracks
        </label>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="scope"
            checked={isModules}
            onChange={() => setType('modules')}
          />
          Modules I choose
        </label>
      </div>
      <Problems problems={problems} />
      {isModules && (
        <ul className="m-0 max-h-80 list-none divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200 px-3 p-0">
          {modules.length === 0 && (
            <li className="py-3 text-sm text-gray-500">
              This cohort has no modules yet.
            </li>
          )}
          {modules.map((module) => (
            <ModuleRow
              key={module.module_id}
              module={module}
              checked={selected.has(module.module_id)}
              onToggle={() => toggle(module.module_id)}
            />
          ))}
        </ul>
      )}
      {!isModules && (
        <div className="space-y-1">
          <p className="text-xs text-gray-600">
            {coreModules.length ? (
              `${plural(coreModules.length, 'core module')}:`
            ) : (
              <>
                No track is marked core yet.{' '}
                {onEditTracks ? (
                  <button
                    type="button"
                    onClick={onEditTracks}
                    className="text-claret-500 hover:underline"
                  >
                    Mark one as core in Specialization Tracks
                  </button>
                ) : (
                  'Mark one as core in Specialization Tracks'
                )}
                , or choose modules instead.
              </>
            )}
          </p>
          {coreModules.length > 0 && (
            <ul className="m-0 list-none divide-y divide-gray-100 rounded-lg border border-gray-200 px-3 p-0">
              {coreModules.map((module) => (
                <ModuleRow key={module.module_id} module={module} readOnly />
              ))}
            </ul>
          )}
        </div>
      )}
    </Section>
  );
}

function RuleCard({ rule, index, onChange, onRemove, problems, unavailable }) {
  const info = RULES.find((r) => r.type === rule.type);
  return (
    <li className="rounded-lg border border-gray-200 p-3 space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900">
            {ruleLabel(rule.type)}
          </p>
          {info?.help && <p className="text-xs text-gray-500">{info.help}</p>}
        </div>
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${ruleLabel(rule.type)}`}
          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
        >
          <DeleteOutlineIcon fontSize="small" />
        </button>
      </div>
      {rule.type !== 'manual_approval' && (
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-xs text-gray-600">
            Target
            <input
              type="number"
              min={1}
              max={100}
              step={1}
              value={rule.target ?? ''}
              aria-label={`${ruleLabel(rule.type)} target`}
              onChange={(e) =>
                onChange({
                  ...rule,
                  target: e.target.value === '' ? '' : Number(e.target.value),
                })
              }
              className={`${INPUT} w-20`}
            />
            %
          </label>
          {rule.type === 'quizzes_passed' && (
            <label className="flex items-center gap-2 text-xs text-gray-600">
              Counts
              <select
                value={rule.attempts || 'any'}
                aria-label="Quiz attempts that count"
                onChange={(e) =>
                  onChange({ ...rule, attempts: e.target.value })
                }
                className={INPUT}
              >
                <option value="any">a pass on any attempt</option>
                <option value="first">only a pass on the first attempt</option>
              </select>
            </label>
          )}
        </div>
      )}
      {unavailable && <p className="text-xs text-amber-700">{unavailable}</p>}
      <Problems problems={problems} />
      <span className="sr-only">Rule {index + 1}</span>
    </li>
  );
}

function RulesSection({
  draft,
  setDraft,
  available,
  ruleProblems,
  combineProblems,
}) {
  const used = new Set(draft.rules.map((r) => r.type));
  const addable = RULES.filter((r) => !used.has(r.type));
  const update = (index, rule) =>
    setDraft((d) => ({
      ...d,
      rules: d.rules.map((r, i) => (i === index ? rule : r)),
    }));
  const remove = (index) =>
    setDraft((d) => ({ ...d, rules: d.rules.filter((_, i) => i !== index) }));

  return (
    <Section
      title="Rules"
      help="What a learner must reach in those modules. Targets are percentages."
    >
      {draft.rules.length === 0 && (
        <p className="text-sm text-gray-500">Add at least one rule.</p>
      )}
      <ul className="m-0 list-none space-y-2 p-0">
        {draft.rules.map((rule, index) => (
          <RuleCard
            // Each rule type is used once, so it identifies the card.
            key={rule.type}
            rule={rule}
            index={index}
            onChange={(next) => update(index, next)}
            onRemove={() => remove(index)}
            problems={ruleProblems[index]}
            unavailable={ruleUnavailable(rule.type, available)}
          />
        ))}
      </ul>
      {addable.length > 0 && (
        <select
          aria-label="Add a rule"
          value=""
          onChange={(e) => {
            const { value } = e.target;
            if (value)
              setDraft((d) => ({ ...d, rules: [...d.rules, newRule(value)] }));
          }}
          className={INPUT}
        >
          <option value="">+ Add a rule…</option>
          {addable.map((rule) => {
            const reason = ruleUnavailable(rule.type, available);
            return (
              <option
                key={rule.type}
                value={rule.type}
                disabled={Boolean(reason)}
              >
                {rule.label}
                {reason ? ` (${reason})` : ''}
              </option>
            );
          })}
        </select>
      )}
      {draft.rules.length > 1 && (
        <div className="space-y-1">
          <p className="text-xs text-gray-600">Learners must meet</p>
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="combine"
                checked={draft.combine === 'all'}
                onChange={() => setDraft((d) => ({ ...d, combine: 'all' }))}
              />
              all of the rules
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="combine"
                checked={draft.combine === 'any'}
                onChange={() => setDraft((d) => ({ ...d, combine: 'any' }))}
              />
              any one of them
            </label>
          </div>
          <Problems problems={combineProblems} />
        </div>
      )}
    </Section>
  );
}

function OptionsSection({ draft, setDraft, available, problems }) {
  const maxTracks = available?.specialization_tracks;
  return (
    <Section title="Choosing">
      <label className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
        Learners can choose up to
        <input
          type="number"
          min={1}
          max={maxTracks || undefined}
          step={1}
          aria-label="Maximum specializations per learner"
          value={draft.max_choices}
          onChange={(e) =>
            setDraft((d) => ({
              ...d,
              max_choices: e.target.value === '' ? '' : Number(e.target.value),
            }))
          }
          onBlur={() =>
            setDraft((d) =>
              Number(d.max_choices) >= 1 ? d : { ...d, max_choices: 1 }
            )
          }
          className={`${INPUT} w-20`}
        />
        specialization{draft.max_choices === 1 ? '' : 's'}
        {maxTracks !== undefined && (
          <span className="text-xs text-gray-500">
            ({plural(maxTracks, 'specialization track')} available)
          </span>
        )}
      </label>
      <label className="flex items-start gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          className="mt-1"
          checked={draft.learners_can_change}
          onChange={(e) =>
            setDraft((d) => ({ ...d, learners_can_change: e.target.checked }))
          }
        />
        <span>
          Learners can switch to another specialization themselves
          <span className="block text-xs text-gray-500">
            Admins can always change a learner&apos;s choice.
          </span>
        </span>
      </label>
      <label className="flex items-start gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          className="mt-1"
          checked={draft.keep_started_on_reset}
          onChange={(e) =>
            setDraft((d) => ({ ...d, keep_started_on_reset: e.target.checked }))
          }
        />
        <span>
          When a choice is undone, keep modules the learner has started
          <span className="block text-xs text-gray-500">
            Progress, quiz results and submissions are always kept, and come
            back if they choose that track again.
          </span>
        </span>
      </label>
      <Problems problems={problems} />
    </Section>
  );
}

function ImpactLine({ children, tone = 'text-gray-700' }) {
  return <li className={`text-sm ${tone}`}>{children}</li>;
}

// Confirm turning on, saving while on, or turning off. Turning on and
// saving while on show the dry run's impact first.
function ConfirmDialog({
  mode,
  cohortId,
  body,
  canSeeLearners,
  saving,
  onConfirm,
  onClose,
}) {
  const [state, setState] = useState({ loading: mode !== 'off' });
  const { data: learners } = useLearners(cohortId, {}, 0, {
    enabled: Boolean(mode) && mode !== 'off' && canSeeLearners,
  });

  useEffect(() => {
    if (!mode || mode === 'off') return undefined;
    let live = true;
    setState({ loading: true });
    checkGate(cohortId, body, { impact: true })
      .then((result) => live && setState({ result }))
      .catch((error) => live && setState({ error }));
    return () => {
      live = false;
    };
    // body is rebuilt each render; the dialog checks once per opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, cohortId]);

  const titles = {
    on: 'Turn on specialization qualification?',
    save: 'Save changes to live settings?',
    off: 'Turn off specialization qualification?',
  };
  const problems = state.result?.problems ?? [];
  const impact = state.result?.impact;
  const withoutChoice = learners?.counts?.enrolled_without_choice;
  const blocked =
    mode !== 'off' && (state.loading || state.error || problems.length > 0);

  return (
    <Modal
      modalOpen={Boolean(mode)}
      onClose={onClose}
      title={titles[mode]}
      small
    >
      <div className="space-y-4 text-left">
        {mode === 'off' && (
          <p className="text-sm text-gray-700">
            Learners in this cohort will be able to choose a specialization
            without meeting the rules. Choices already made stay. The settings
            are kept for when you turn it back on.
          </p>
        )}
        {mode !== 'off' && state.loading && (
          <p className="text-sm text-gray-500">Checking every learner…</p>
        )}
        {state.error && (
          <p className="text-sm text-red-700">
            {specError(state.error, "We couldn't check these settings.")}
          </p>
        )}
        {problems.length > 0 && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-1">
            <p className="text-sm font-medium text-red-800">Fix these first:</p>
            <Problems problems={problems} />
          </div>
        )}
        {impact && problems.length === 0 && (
          <ul className="m-0 list-none space-y-1.5 p-0">
            <ImpactLine>
              {plural(impact.members, 'learner')} in the cohort;{' '}
              <strong>{impact.would_qualify.toLocaleString()}</strong> would
              qualify under these settings.
            </ImpactLine>
            {impact.chosen > 0 && (
              <ImpactLine>
                {plural(impact.chosen, 'learner has', 'learners have')} already
                chosen. Choices are never undone automatically.
              </ImpactLine>
            )}
            {impact.would_lose > 0 && (
              <ImpactLine tone="text-amber-800">
                {plural(impact.would_lose, 'learner')} who qualify now but
                haven&apos;t chosen yet would stop qualifying.
              </ImpactLine>
            )}
            {withoutChoice > 0 && (
              <ImpactLine>
                {plural(withoutChoice, 'learner')} already{' '}
                {withoutChoice === 1 ? 'has' : 'have'} specialization modules
                without a choice; they keep them.
              </ImpactLine>
            )}
            {mode === 'on' && (
              <ImpactLine tone="text-gray-500">
                From then on, specialization modules can only be joined by
                choosing them.
              </ImpactLine>
            )}
          </ul>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={SECONDARY}>
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={blocked || saving}
            className={PRIMARY}
          >
            {saving
              ? 'Saving…'
              : { on: 'Turn on', save: 'Save changes', off: 'Turn off' }[mode]}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default function GateEditor({
  cohortId,
  gate,
  onReload,
  canSeeLearners,
  onEditTracks,
}) {
  const save = useSaveGate(cohortId);
  // Keyed on what was saved, so refreshing modules (e.g. after editing
  // tracks) keeps unsaved edits; a save or a reload replaces them.
  const savedKey = `${gate.updated_at}|${JSON.stringify(gate.config)}`;
  const baseline = useMemo(
    () => normaliseConfig(gate.config),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [savedKey]
  );
  const [draft, setDraft] = useState(baseline);
  const [check, setCheck] = useState({
    problems: [],
    available: gate.available,
  });
  const [checking, setChecking] = useState(false);
  const [checkFailed, setCheckFailed] = useState(false);
  const [saveProblems, setSaveProblems] = useState(null);
  const [conflict, setConflict] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const sequence = useRef(0);

  // A fresh load (or a save) replaces the draft.
  useEffect(() => {
    setDraft(baseline);
    setConflict(null);
  }, [baseline]);

  const apiConfig = useMemo(() => configForApi(draft), [draft]);
  const configKey = JSON.stringify(apiConfig);
  const dirty = !sameConfig(draft, baseline);

  // Dry-run the draft as it changes: problems and what its modules offer.
  useEffect(() => {
    setSaveProblems(null);
    const id = sequence.current + 1;
    sequence.current = id;
    setChecking(true);
    const timer = setTimeout(() => {
      checkGate(cohortId, { enabled: gate.enabled, config: apiConfig })
        .then((result) => {
          if (sequence.current !== id) return;
          setCheck({
            problems: result?.problems ?? [],
            available: result?.available,
          });
          setCheckFailed(false);
        })
        .catch(() => {
          if (sequence.current === id) setCheckFailed(true);
        })
        .finally(() => {
          if (sequence.current === id) setChecking(false);
        });
    }, CHECK_DELAY_MS);
    return () => clearTimeout(timer);
    // configKey stands for apiConfig; a refreshed gate may have new tracks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configKey, cohortId, gate]);

  const problems = saveProblems ?? check.problems;
  const grouped = groupProblems(problems);
  const available = check.available ?? gate.available;

  const put = (body, success) =>
    save.mutate(
      {
        ...body,
        ...(gate.updated_at ? { expected_updated_at: gate.updated_at } : {}),
      },
      {
        onSuccess: () => {
          setConfirm(null);
          toast.success(success);
        },
        onError: (error) => {
          setConfirm(null);
          const code = errorCode(error);
          const data = error?.response?.data;
          if (code === 'GATE_INVALID') {
            setSaveProblems(data?.details?.problems ?? []);
            toast.error(specError(error, 'Some settings need fixing.'));
          } else if (code === 'GATE_CHANGED') {
            setConflict({ message: specError(error), ...data?.details });
          } else {
            toast.error(specError(error, "We couldn't save these settings."));
          }
        },
      }
    );

  const confirmBody = { enabled: confirm !== 'off', config: apiConfig };
  const runConfirm = () => {
    if (confirm === 'off') {
      put({ enabled: false }, 'Specialization qualification is off.');
    } else if (confirm === 'on') {
      put(confirmBody, 'Specialization qualification is on.');
    } else {
      put(confirmBody, 'Settings saved.');
    }
  };

  return (
    <div className="space-y-5">
      <section
        className={`${CARD} flex flex-wrap items-center justify-between gap-4 px-5 py-4`}
      >
        <div className="space-y-0.5">
          <div className="flex items-center gap-2">
            <span
              className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                gate.enabled
                  ? 'bg-green-50 text-green-700'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {gate.enabled ? 'On' : 'Off'}
            </span>
            <p className="text-sm text-gray-900">
              {gate.enabled
                ? 'Learners must meet these rules before choosing a specialization.'
                : 'Off: learners choose a specialization without meeting any rules.'}
            </p>
          </div>
          <p className="text-xs text-gray-500">
            {gate.updated_at
              ? `Last saved ${formatDateTime(gate.updated_at)}${
                  gate.updated_by ? ` by ${gate.updated_by}` : ''
                }.`
              : 'Not set up yet. These are the defaults.'}
            {dirty && ' You have unsaved changes.'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {dirty && (
            <button
              type="button"
              onClick={() => setDraft(baseline)}
              className={SECONDARY}
            >
              Discard changes
            </button>
          )}
          {!gate.enabled && (
            <>
              <button
                type="button"
                disabled={!dirty || save.isLoading}
                onClick={() =>
                  put(
                    { enabled: false, config: apiConfig },
                    'Draft saved. Qualification is still off.'
                  )
                }
                className={SECONDARY}
              >
                Save draft
              </button>
              <button
                type="button"
                disabled={checking || problems.length > 0 || save.isLoading}
                onClick={() => setConfirm('on')}
                className={PRIMARY}
              >
                Turn on…
              </button>
            </>
          )}
          {gate.enabled && (
            <>
              <button
                type="button"
                disabled={save.isLoading}
                onClick={() => setConfirm('off')}
                className={SECONDARY}
              >
                Turn off…
              </button>
              <button
                type="button"
                disabled={
                  !dirty || checking || problems.length > 0 || save.isLoading
                }
                onClick={() => setConfirm('save')}
                className={PRIMARY}
              >
                Save changes…
              </button>
            </>
          )}
        </div>
      </section>

      {conflict && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <span>
            {conflict.message}
            {conflict.updated_by &&
              ` (${conflict.updated_by}${
                conflict.updated_at
                  ? `, ${formatDateTime(conflict.updated_at)}`
                  : ''
              })`}
          </span>
          <button type="button" onClick={onReload} className={SECONDARY}>
            Reload settings
          </button>
        </div>
      )}

      {(problems.length > 0 || checkFailed) && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 space-y-1">
          <p className="text-sm font-medium text-red-800">
            {checkFailed && !problems.length
              ? "We couldn't check these settings just now. They're checked again when you save."
              : `${plural(
                  problems.length,
                  'setting needs',
                  'settings need'
                )} fixing before ${gate.enabled ? 'saving' : 'turning on'}.`}
          </p>
          <Problems problems={grouped.general} />
        </div>
      )}

      <ScopeSection
        onEditTracks={onEditTracks}
        draft={draft}
        setDraft={setDraft}
        modules={gate.modules ?? []}
        problems={grouped.scope}
      />
      {available && (
        <p className="-mt-2 px-1 text-xs text-gray-500">
          {checking ? 'Checking… ' : ''}
          These modules have {plural(available.weeks, 'week')} of content,{' '}
          {plural(available.quizzes, 'quiz', 'quizzes')} and{' '}
          {plural(available.assignments, 'assignment')}.
        </p>
      )}
      <RulesSection
        draft={draft}
        setDraft={setDraft}
        available={available}
        ruleProblems={grouped.rules}
        combineProblems={grouped.combine}
      />
      <OptionsSection
        draft={draft}
        setDraft={setDraft}
        available={available}
        problems={grouped.options}
      />

      {confirm && (
        <ConfirmDialog
          mode={confirm}
          cohortId={cohortId}
          body={confirmBody}
          canSeeLearners={canSeeLearners}
          saving={save.isLoading}
          onConfirm={runConfirm}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
